import React from "react";
import { Check, Crown, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { pricingFragmented, pricingAllInOne, subscription, STRIPE_CHECKOUT_URL } from "../mock";

const Pricing = () => {
  return (
    <section id="pricing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
            Stop paying to stay fragmented
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-black leading-[1.05]">
            One platform.{" "}
            <em className="text-teal-700 font-serif font-normal">
              Everything included.
            </em>
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Fragmented */}
          <div className="rounded-2xl border border-black/10 bg-white p-8 md:p-10 relative">
            <div className="text-xs uppercase tracking-wide text-black/50 font-medium">
              What most organizations juggle
            </div>
            <div className="mt-4 text-2xl md:text-3xl font-semibold text-black">
              The patchwork stack
            </div>
            <div className="mt-8 space-y-3">
              {pricingFragmented.map((p) => (
                <div
                  key={p.label}
                  className="flex items-center justify-between py-3 border-b border-black/10 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-black/5 flex items-center justify-center">
                      <X className="w-3 h-3 text-black/40" />
                    </span>
                    <span className="text-black/75">{p.label}</span>
                  </div>
                  <span className="text-black/60 font-mono text-sm">
                    {p.price}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-black/10 flex items-end justify-between">
              <div>
                <div className="text-xs text-black/50 uppercase tracking-wide">
                  Monthly total
                </div>
                <div className="text-4xl font-semibold text-black mt-1">
                  $245
                </div>
                <div className="text-xs text-black/50 mt-1">
                  per month, minimum
                </div>
              </div>
            </div>
          </div>

          {/* All-in-one */}
          <div className="rounded-2xl bg-black text-white p-8 md:p-10 relative overflow-hidden">
            <div
              className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-30 blur-3xl"
              style={{
                background:
                  "radial-gradient(circle, #14b8a6 0%, transparent 70%)",
              }}
            />
            <div className="relative">
              <div className="text-xs uppercase tracking-wide text-teal-300 font-medium">
                Good Fruit brings it together
              </div>
              <div className="mt-4 text-2xl md:text-3xl font-semibold">
                One connected Hub
              </div>
              <div className="mt-8 space-y-3">
                {pricingAllInOne.map((p) => (
                  <div
                    key={p}
                    className="flex items-center gap-3 py-3 border-b border-white/10 last:border-0"
                  >
                    <span className="w-5 h-5 rounded-full bg-teal-400 text-black flex items-center justify-center">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-white/90">{p}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs text-teal-300 uppercase tracking-wide">
                    Start building
                  </div>
                  <div className="text-4xl font-semibold mt-1">Free</div>
                  <div className="text-xs text-white/50 mt-1">
                    Upgrade when you&apos;re growing
                  </div>
                </div>
                <Button
                  className="bg-teal-400 hover:bg-teal-300 text-black font-semibold h-11 px-6"
                  onClick={() =>
                    document
                      .getElementById("cta")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Build for free
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Optional Premium upgrade */}
        <div className="mt-8 rounded-2xl border border-teal-600/30 bg-gradient-to-br from-teal-50 to-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-black text-teal-300 flex items-center justify-center shrink-0">
              <Crown className="w-5 h-5" />
            </div>
            <div>
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-teal-700 font-semibold">
                Optional add-on · {subscription.highlight}
              </div>
              <div className="mt-1 text-xl md:text-2xl font-semibold text-black tracking-tight">
                {subscription.name}{" "}
                <span className="text-teal-700">
                  · ${subscription.price}/{subscription.cadence}
                </span>
              </div>
              <div className="mt-1 text-sm text-black/60 max-w-xl">
                Unlock the full credit curriculum plus partner access to
                IdentityIQ credit monitoring and CreditScoreIQ DIY repair
                tools. Cancel anytime.
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link to="/finance-course#credit-mastery">
              <Button
                variant="outline"
                className="bg-white text-black border border-black/20 hover:border-teal-700 hover:text-teal-700 font-semibold h-11 px-5 w-full sm:w-auto"
              >
                Learn more
              </Button>
            </Link>
            <a
              href={STRIPE_CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-black hover:bg-teal-700 text-white font-semibold h-11 px-6 w-full sm:w-auto">
                Subscribe — ${subscription.price}/mo
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
